import React, {useEffect, useRef, useState} from "react";
import toast from "react-hot-toast";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import moment from "moment";
import {FallbackImage, Input, Select} from "../../../components";
import {GeneralService, UserService, VehicleService} from "../../../services";
import {sanitizeToNumbers, classNames, Constants} from "../../../utils";
import {
  BackIcon,
  FormTab1Icon,
  FormTab2ActiveIcon,
  FormTab2Icon,
  PasswordActive,
  PasswordInactive,
  PlaceholderUserIcon,
  UploadFileIcon,
} from "../../../assets/icons";
import {SelectOwner} from "./select-owner";

const STEP = {
  DRIVER: 1,
  VEHICLE: 2,
};

const ModalCreateDriver = ({onClose, onSuccess, initialData}) => {
  const {
    form,
    handleImageChange,
    isLoading,
    nextStep,
    onChange,
    selects,
    selectSuggestion,
    setSelects,
    setStep,
    step,
  } = useForm({initialData, onClose, onSuccess});
  const [showPassword, setShowPassword] = useState(false);
  const isEdit = !!initialData?.id;

  return (
    <div className="w-[48rem] bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4">
        <h2 className="text-lg font-bold text-text">
          {isEdit ? "Editar" : "Crear"} Conductor
        </h2>
        <button
          type="button"
          className="p-1.5 rounded-full hover:bg-gray5 transition-colors"
          onClick={onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 text-text"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="flex justify-center items-center text-orange font-bold text-lg mb-4">
        <img
          src={FormTab1Icon}
          alt="Tab"
          className="w-6 h-6 object-contain mr-2"
        />
        <span>Conductor</span>
        <hr
          className={classNames(
            "w-8 mx-4",
            step === STEP.VEHICLE ? "border-orange" : "border-gray6",
          )}
        />
        <img
          className="w-6 h-6 object-contain mr-2"
          src={step === STEP.VEHICLE ? FormTab2ActiveIcon : FormTab2Icon}
          alt="Tab"
        />
        <span
          className={classNames(
            step === STEP.VEHICLE ? "text-orange" : "text-gray6",
          )}
        >
          Vehículo
        </span>
      </div>

      {step === STEP.VEHICLE && (
        <button className="ml-6" onClick={() => setStep(STEP.DRIVER)}>
          <img
            src={BackIcon}
            className="w-5 h-5 object-contain invert"
            alt="Volver"
          />
        </button>
      )}

      {step === STEP.DRIVER && (
        <div className="grid grid-cols-2 gap-4 px-6">
          <Input
            label="Nombre"
            value={form.person.name || ""}
            onValueChange={(v) => onChange("person.name", v)}
          />
          <div>
            <p className="mb-1 text-text text-sm">C.I.</p>
            <div className="flex items-center border border-gray6 bg-white h-10 w-full rounded-full overflow-hidden">
              <input
                type="text"
                className="flex-1 h-full outline-none border-none bg-transparent px-4 text-sm"
                placeholder="Número de documento"
                value={form.person.document || ""}
                onChange={(e) =>
                  onChange("person.document", sanitizeToNumbers(e.target.value))
                }
              />
            </div>
          </div>
          <Input
            label="Correo"
            value={form.email || ""}
            onValueChange={(v) => onChange("email", v)}
          />
          <Input
            label="Teléfono"
            value={form.person.phone || ""}
            onValueChange={(v) =>
              onChange("person.phone", sanitizeToNumbers(v))
            }
            maxLength={11}
          />
          <Input
            className={{base: "col-span-2"}}
            label="Dirección"
            value={form.person?.address_house || ""}
            onValueChange={(v) => onChange("person.address_house", v)}
          />

          <div className="col-span-2 mt-2">
            <p className="text-lg font-semibold text-black">Ubicación</p>
          </div>

          <div id="map-create-driver" className="max-h-96 aspect-square" />

          <PlacesAutocomplete
            value={form._address || ""}
            onChange={(v) => onChange("_address", v)}
            onSelect={selectSuggestion}
            searchOptions={{componentRestrictions: {country: ["ve"]}}}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => (
              <div className="w-full relative">
                <div className="w-full">
                  <div className="flex items-center gap-x-4 border border-gray6 bg-white h-10 w-full rounded-full px-5 py-3">
                    <input
                      placeholder="Buscar lugar"
                      name="_address"
                      {...getInputProps({
                        className:
                          "flex-1 outline-none border-none active:outline-none bg-transparent w-full",
                      })}
                    />
                  </div>
                </div>
                <div className="p-2 mt-2 bg-white absolute z-10 w-full shadow-md rounded-lg empty:hidden">
                  {loading && <div className="loading">Cargando...</div>}
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.placeId}
                      {...getSuggestionItemProps(suggestion, {
                        className: classNames(
                          "cursor-pointer bg-white hover:font-normal hover:bg-black/10",
                          suggestion.active ? "bg-gray6 font-semibold" : "",
                        ),
                      })}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </PlacesAutocomplete>

          <hr className="col-span-2 border-orange" />

          <div>
            <p>Licencia</p>
            <Select
              placeholder="Seleccione tipo"
              value={form.driver.license_type || ""}
              items={selects.licenseType}
              onValueChange={(v) => onChange("driver.license_type", v)}
            />
          </div>
          <Input
            type="date"
            label="Fecha de vencimiento"
            value={form.driver.license_expiration || ""}
            onValueChange={(v) => onChange("driver.license_expiration", v)}
          />

          <label>
            <p className="mb-1 text-text text-sm">Foto de licencia</p>
            <div
              className={classNames(
                "flex items-center gap-x-4 border h-10 w-full rounded-full p-3 cursor-pointer",
                form.license_photo
                  ? "border-green bg-green"
                  : "border-gray4 bg-gray4",
              )}
            >
              <img src={UploadFileIcon} className="w-6 h-6" alt="" />
              <span className="flex-1 outline-none border-none bg-transparent w-full text-sm text-white line-clamp-1 truncate">
                {form.license_photo
                  ? form.license_photo?.name || "foto de licencia.jpg"
                  : "Subir imagen"}
              </span>
            </div>
            <input
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) onChange("license_photo", file);
              }}
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </label>

          <hr className="col-span-2 border-orange" />

          <Input
            label="Número certificado médico"
            value={form.driver.medical_cert_type || ""}
            onValueChange={(v) => onChange("driver.medical_cert_type", v)}
          />
          <Input
            type="date"
            label="Fecha de vencimiento"
            value={form.driver.medical_cert_expiration || ""}
            onValueChange={(v) => onChange("driver.medical_cert_expiration", v)}
          />

          <label>
            <p className="mb-1 text-text text-sm">
              Certificado médico (archivo)
            </p>
            <div
              className={classNames(
                "flex items-center gap-x-4 border h-10 w-full rounded-full p-3 cursor-pointer",
                form.medical_cert_photo
                  ? "border-green bg-green"
                  : "border-gray4 bg-gray4",
              )}
            >
              <img src={UploadFileIcon} className="w-5 h-5" alt="" />
              <span className="flex-1 outline-none border-none bg-transparent w-full text-sm text-white line-clamp-1 truncate">
                {form.medical_cert_photo
                  ? form.medical_cert_photo?.name || "Foto de certificado.jpg"
                  : "Subir imagen"}
              </span>
            </div>
            <input
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) onChange("medical_cert_photo", file);
              }}
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </label>

          <div className="col-span-2 mt-2">
            <p className="text-lg font-semibold text-black">Contraseña</p>
          </div>
          <Input
            label="Contraseña"
            type={showPassword ? "text" : "password"}
            value={form.password || ""}
            onValueChange={(v) => onChange("password", v)}
            append={
              <button
                type="button"
                tabIndex="-1"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400"
              >
                <img
                  src={showPassword ? PasswordActive : PasswordInactive}
                  className="w-6 h-6"
                  alt=""
                />
              </button>
            }
          />
          <Input
            label="Repetir contraseña"
            type={showPassword ? "text" : "password"}
            value={form.password_confirmation || ""}
            onValueChange={(v) => onChange("password_confirmation", v)}
            append={
              <button
                type="button"
                tabIndex="-1"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400"
              >
                <img
                  src={showPassword ? PasswordActive : PasswordInactive}
                  className="w-6 h-6"
                  alt=""
                />
              </button>
            }
          />
        </div>
      )}

      {step === STEP.VEHICLE && (
        <div className="grid grid-cols-2 gap-4 px-6 mt-4">
          <section className="col-span-2">
            <p className="font-bold text-sm mb-2">El vehículo pertenece a</p>
            <div className="flex gap-8">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="belongs_to"
                  className="w-5 h-5 accent-[#22c55e]"
                  checked={
                    form.vehicle.belongs_to === Constants.BELONGS_TO.MAXER
                  }
                  onChange={() =>
                    onChange("vehicle.belongs_to", Constants.BELONGS_TO.MAXER)
                  }
                />
                <span className="text-text font-medium">Maxer</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="belongs_to"
                  className="w-5 h-5 accent-[#22c55e]"
                  checked={
                    form.vehicle.belongs_to === Constants.BELONGS_TO.OWNER
                  }
                  onChange={() =>
                    onChange("vehicle.belongs_to", Constants.BELONGS_TO.OWNER)
                  }
                />
                <span className="text-text font-medium">Propietario</span>
              </label>
            </div>
          </section>

          {form.vehicle.belongs_to === Constants.BELONGS_TO.OWNER && (
            <div className="space-y-1">
              <p className="text-sm text-text">Propietario</p>
              <SelectOwner
                value={form.vehicle.owner_id}
                displayName={form.vehicle.owner_name}
                onSelect={(id, name, user) => {
                  onChange("vehicle.owner_id", id);
                  onChange("vehicle.owner_name", name);
                  onChange("driver.vehicle_id", "");
                  setSelects((s) => ({
                    ...s,
                    ownerVehicles: user?.owner_vehicles || [],
                  }));
                }}
                placeholder="Buscar propietario..."
              />
            </div>
          )}

          <div className="space-y-1">
            <p className="text-sm text-text">Vehículo</p>
            <Select
              value={form.driver.vehicle_id}
              items={
                (form.vehicle.belongs_to === Constants.BELONGS_TO.OWNER
                  ? selects.ownerVehicles
                  : selects.maxerVehicles
                )?.map((x) => ({
                  value: x?.id,
                  label: `${x?.brand || ""} ${x?.model || ""} - ${x?.plaque || ""}`,
                })) || []
              }
              onValueChange={(v) => onChange("driver.vehicle_id", v)}
              placeholder="Seleccione un vehículo"
            />
          </div>

          <label className="flex items-center my-4 col-span-2">
            <div
              className={classNames(
                "flex items-center justify-center bg-[#F5F7FA] border rounded-full w-24 h-24 cursor-pointer overflow-hidden",
                form.photo_blob ? "border-orange" : "border-gray6",
              )}
            >
              <FallbackImage
                src={form.photo_blob}
                placeholder={PlaceholderUserIcon}
                className="w-full h-full object-cover"
                placeholderClassName="w-12 h-12"
                alt="Foto del conductor"
              />
            </div>
            <p className="ml-4 text-sm">Foto del conductor</p>
            <input
              type="file"
              hidden
              onChange={handleImageChange}
              accept=".jpg,.jpeg,.png"
            />
          </label>
        </div>
      )}

      <div className="flex justify-center mt-6 px-6 py-4 gap-3">
        <button
          type="button"
          className="px-6 py-2 bg-gray-200 text-text font-semibold rounded-full hover:bg-gray-300 transition-colors text-sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cerrar
        </button>
        <button
          type="button"
          className="px-6 py-2 bg-red text-white font-semibold rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
          onClick={nextStep}
          disabled={isLoading}
        >
          {step === STEP.VEHICLE
            ? isLoading
              ? "Guardando..."
              : "Guardar"
            : "Siguiente"}
        </button>
      </div>
    </div>
  );
};

const useForm = ({initialData, onClose, onSuccess}) => {
  const isEdit = !!initialData?.id;
  const map = useRef();

  const getInitial = (data) => ({
    id: data?.id || "",
    email: data?.email || "",
    password: "",
    password_confirmation: "",
    person: {
      name: data?.person?.name || "",
      phone: data?.person?.phone || "",
      document: data?.person?.document || "",
      address_house: data?.person?.address_house || "",
      latitude: data?.person?.latitude || "",
      longitude: data?.person?.longitude || "",
    },
    driver: {
      vehicle_id: data?.person?.driver?.vehicle?.id || "",
      license_type: data?.person?.driver?.license_type || "",
      license_expiration: data?.person?.driver?.license_expiration
        ? moment(data?.person?.driver?.license_expiration).format("YYYY-MM-DD")
        : "",
      medical_cert_type: data?.person?.driver?.medical_cert_type || "",
      medical_cert_expiration: data?.person?.driver?.medical_cert_expiration
        ? moment(data?.person?.driver?.medical_cert_expiration).format(
            "YYYY-MM-DD",
          )
        : "",
    },
    vehicle: {
      belongs_to:
        data?.person?.driver?.vehicle?.belongs_to || Constants.BELONGS_TO.MAXER,
      owner_id: data?.person?.driver?.vehicle?.owner_id ?? "",
      owner_name: data?.person?.driver?.vehicle?.owner?.person
        ? data?.person?.driver?.vehicle?.owner.person.fullName
        : "",
    },
    photo: data?.person?.photo || "",
    photo_blob: data?.person?.photo || null,
    license_photo: data?.person?.driver?.license || "",
    medical_cert_photo: data?.person?.driver?.medical_cert || "",
    hasFile: true,
    _address: "",
  });

  const [step, setStep] = useState(STEP.DRIVER);
  const [form, setForm] = useState(() => getInitial(initialData));
  const [selects, setSelects] = useState({
    documentTypes: [],
    maxerVehicles: [],
    licenseType: [],
    ownerVehicles: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const getSelects = async () => {
    try {
      const maxerVehiclesResp = await VehicleService.admin.findAll({
        belongs_to: Constants.BELONGS_TO.MAXER,
      });

      setSelects((s) => ({
        ...s,
        maxerVehicles: maxerVehiclesResp?.data || maxerVehiclesResp || [],
        licenseType: [
          {value: "Primera", label: "Primera"},
          {value: "Segunda", label: "Segunda"},
          {value: "Tercera", label: "Tercera"},
          {value: "Cuarta", label: "Cuarta"},
          {value: "Quinta", label: "Quinta"},
        ],
      }));
    } catch (error) {
      console.log(error);
    }
  };
  const setMap = () => {
    const el = document.getElementById("map-create-driver");
    if (!el) return;

    map.current = new window.google.maps.Map(el, {
      zoom: 13,
      center: new window.google.maps.LatLng(
        form.person?.latitude || 10.503703739685555,
        form.person?.longitude || -66.91201470063201,
      ),
      draggable: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false,
    });

    const marker = new window.google.maps.Marker({
      position: map.current.getCenter(),
      map: map.current,
      title: "",
      animation: window.google.maps.Animation.DROP,
      draggable: false,
    });

    window.google.maps.event.addListener(map.current, "center_changed", () => {
      marker.setPosition(map.current.getCenter());
    });
  };

  const selectSuggestion = async (address) => {
    try {
      const results = await geocodeByAddress(address);
      if (!results.length) return;
      const latLng = await getLatLng(results[0]);
      map.current.setCenter(latLng);
      onChange("person.address_house", address);
      onChange("person.latitude", latLng.lat);
      onChange("person.longitude", latLng.lng);
    } catch (error) {
      console.error("Error", error);
    }
  };

  const onChange = (target, value) => {
    if (target.startsWith("person.")) {
      const key = target.split(".")[1];
      setForm((s) => ({...s, person: {...s.person, [key]: value}}));
    } else if (target.startsWith("driver.")) {
      const key = target.split(".")[1];
      setForm((s) => ({...s, driver: {...s.driver, [key]: value}}));
    } else if (target.startsWith("vehicle.")) {
      const key = target.split(".")[1];
      setForm((s) => {
        const newState = {...s, vehicle: {...s.vehicle, [key]: value}};
        if (key === "belongs_to") {
          newState.driver.vehicle_id = "";
          newState.vehicle.owner_id = "";
        }
        return newState;
      });
    } else {
      setForm((s) => ({...s, [target]: value}));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((s) => ({
      ...s,
      photo: file,
      photo_blob: URL.createObjectURL(file),
    }));
  };

  const nextStep = () => {
    if (!isValidForm()) return;
    if (step === STEP.DRIVER) return setStep(STEP.VEHICLE);
    if (step === STEP.VEHICLE) return submit();
  };

  const isValidForm = () => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    };

    if (step === STEP.DRIVER) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!String(form.person.name).trim())
        return onError("El nombre es requerido");
      if (!String(form.person.document).trim())
        return onError("El número de C.I. es requerido");
      if (form.person.document.length < 5)
        return onError("El número de C.I. debe tener al menos 5 caracteres");
      if (!String(form.email).trim())
        return onError("El correo electrónico es requerido");
      if (!emailRegex.test(form.email))
        return onError("El correo electrónico no es válido");
      if (!String(form.person.phone).trim())
        return onError("El teléfono es requerido");
      if (form.person.phone.length < 7)
        return onError("El teléfono debe tener al menos 7 dígitos");
      if (!form.person.address_house)
        return onError("La dirección es requerida");
      if (!form.driver.license_type)
        return onError("El tipo de licencia es requerido");
      if (!form.driver.license_expiration)
        return onError("La fecha de vencimiento de la licencia es requerida");
      if (!form.license_photo && !form.license)
        return onError("La foto de la licencia es requerida");
      if (!String(form.driver.medical_cert_type).trim())
        return onError("El número de certificado médico es requerido");
      if (!form.driver.medical_cert_expiration)
        return onError(
          "La fecha de vencimiento del certificado médico es requerida",
        );
      if (!form.medical_cert_photo && !form.medical_cert)
        return onError("El archivo del certificado médico es requerido");
      if (!isEdit && !form.password)
        return onError("La contraseña es requerida");
      if (form.password && form.password.length < 6)
        return onError("La contraseña debe tener al menos 6 caracteres");
      if (form.password !== form.password_confirmation)
        return onError("Las contraseñas no coinciden");
    }

    if (step === STEP.VEHICLE) {
      if (!form.vehicle.belongs_to)
        return onError("Debe seleccionar a quién pertenece el vehículo");
      if (
        form.vehicle.belongs_to === Constants.BELONGS_TO.OWNER &&
        !form.vehicle.owner_id
      )
        return onError("Debe seleccionar un propietario");
      if (!form.driver.vehicle_id)
        return onError("Debe seleccionar un vehículo");
      if (!form.photo) return onError("Debe subir la foto del conductor");
    }

    return true;
  };

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      const {photo_blob, ...formData} = form;
      const payload = {
        ...formData,
        person: JSON.stringify({
          ...form.person,
          latitude: map.current.getCenter().lat(),
          longitude: map.current.getCenter().lng(),
        }),
        driver: JSON.stringify(form.driver),
        vehicle: JSON.stringify(form.vehicle),
      };

      if (isEdit) {
        await UserService.admin.updateDriver(payload);
        toast.success("Conductor actualizado con éxito");
      } else {
        await UserService.admin.createDriver(payload);
        toast.success("Conductor creado con éxito");
      }
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (step !== STEP.DRIVER) return;
    setTimeout(() => setMap(), 1000);
  }, [step]);

  useEffect(() => {
    getSelects();
  }, []);

  return {
    form,
    handleImageChange,
    isLoading,
    nextStep,
    onChange,
    selects,
    selectSuggestion,
    setSelects,
    setStep,
    step,
    submit,
  };
};

export default ModalCreateDriver;
